/**
 * Unified Video Player for Landing Sections
 * Handles YouTube, Vimeo, Wistia, and direct video URLs.
 * Usage: new LandingVideoPlayer({ ... })
 */
(function() {
  'use strict';

  /* ---- Shared API Loaders (one per page) ---- */

  function loadYTApi(callback) {
    window._lvpYTQueue = window._lvpYTQueue || [];
    window._lvpYTQueue.push(callback);

    if (window.YT && window.YT.Player) {
      window._lvpYTQueue.forEach(function(cb) { cb(); });
      window._lvpYTQueue = [];
      return;
    }

    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) return;

    var tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);

    var origCb = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function() {
      if (origCb) origCb();
      (window._lvpYTQueue || []).forEach(function(cb) { cb(); });
      window._lvpYTQueue = [];
    };
  }

  function loadWistiaApi(callback) {
    if (window.Wistia) { callback(); return; }

    if (!document.querySelector('script[src*="fast.wistia.com/assets/external/E-v1.js"]')) {
      var tag = document.createElement('script');
      tag.src = 'https://fast.wistia.com/assets/external/E-v1.js';
      tag.async = true;
      document.head.appendChild(tag);
    }

    var check = setInterval(function() {
      if (window.Wistia) {
        clearInterval(check);
        callback();
      }
    }, 100);
  }

  /* ---- URL Helpers ---- */

  function getVideoType(url) {
    if (!url) return null;
    if (url.indexOf('youtu') > -1) return 'youtube';
    if (url.indexOf('vimeo') > -1) return 'vimeo';
    if (url.indexOf('wistia') > -1) return 'wistia';
    return 'direct';
  }

  function getYouTubeId(url) {
    var m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : '';
  }

  function getVimeoId(url) {
    var m = url.match(/vimeo\.com\/(\d+)/);
    return m ? m[1] : '';
  }

  function getWistiaId(url) {
    var m = url.match(/(?:wistia\.com\/medias\/|wistia\.net\/embed\/iframe\/|wistia\.com\/embed\/iframe\/)([a-zA-Z0-9]+)/);
    return m ? m[1] : '';
  }

  /* ---- Instance Registry (only one video plays at a time) ---- */
  var allInstances = [];

  function pauseAllExcept(activeInstance) {
    allInstances.forEach(function(inst) {
      if (inst !== activeInstance && inst._isPlaying) {
        inst._pause();
      }
    });
  }

  /* ---- Constructor ---- */

  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.playerEl      – The player container element
   * @param {string}      opts.videoUrl      – Video URL
   * @param {string}      opts.uid           – Unique id for this instance
   * @param {HTMLElement} [opts.pillEl]      – Audio pill button (stories section)
   * @param {HTMLElement} [opts.progressEl]  – Progress bar element (stories section)
   */
  function LandingVideoPlayer(opts) {
    this.playerEl = opts.playerEl;
    this.videoUrl = opts.videoUrl || '';
    this.uid = opts.uid;
    this.pillEl = opts.pillEl || null;
    this.progressEl = opts.progressEl || null;

    this._ytPlayer = null;
    this._videoEl = null;
    this._wistiaVideo = null;
    this._isPlaying = false;
    this._initialized = false;
    this._progressRAF = null;
    this._overlay = null;

    allInstances.push(this);
    this._bindEvents();
  }

  var proto = LandingVideoPlayer.prototype;

  /* ---- Public ---- */

  proto.play = function() {
    if (this._initialized) {
      this._toggle();
    } else {
      this._init();
    }
  };

  proto.destroy = function() {
    this._stopProgress();
    if (this._ytPlayer && this._ytPlayer.destroy) this._ytPlayer.destroy();
    this._ytPlayer = null;
    this._videoEl = null;
    this._wistiaVideo = null;
  };

  /* ---- Private: Events ---- */

  proto._bindEvents = function() {
    var self = this;

    this.playerEl.addEventListener('click', function(e) {
      if (e.target.tagName === 'IFRAME') return;
      self.play();
    });

    if (this.pillEl) {
      this.pillEl.addEventListener('click', function() {
        self.play();
      });
    }
  };

  /* ---- Private: State ---- */

  proto._pause = function() {
    if (this._ytPlayer && typeof this._ytPlayer.getPlayerState === 'function') {
      if (this._ytPlayer.getPlayerState() === YT.PlayerState.PLAYING) {
        this._ytPlayer.pauseVideo();
      }
    } else if (this._wistiaVideo) {
      if (this._wistiaVideo.state() === 'playing') {
        this._wistiaVideo.pause();
      }
    } else if (this._videoEl && !this._videoEl.paused) {
      this._videoEl.pause();
    }
  };

  proto._setPlaying = function(playing) {
    this._isPlaying = playing;

    if (this.pillEl) this.pillEl.classList.toggle('is-playing', playing);

    if (playing) {
      pauseAllExcept(this);
      this.playerEl.classList.add('is-playing');
      this._startProgress();
    } else {
      this._stopProgress();
    }
  };

  /* ---- Private: Progress ---- */

  proto._updateProgress = function(current, duration) {
    if (!this.progressEl || !duration || duration <= 0) return;
    var pct = Math.min((current / duration) * 100, 100);
    this.progressEl.style.width = pct + '%';
  };

  proto._trackProgress = function() {
    if (!this._isPlaying) return;

    if (this._ytPlayer && typeof this._ytPlayer.getCurrentTime === 'function') {
      this._updateProgress(this._ytPlayer.getCurrentTime(), this._ytPlayer.getDuration());
    } else if (this._wistiaVideo && typeof this._wistiaVideo.time === 'function') {
      this._updateProgress(this._wistiaVideo.time(), this._wistiaVideo.duration());
    } else if (this._videoEl && !isNaN(this._videoEl.duration)) {
      this._updateProgress(this._videoEl.currentTime, this._videoEl.duration);
    }

    var self = this;
    this._progressRAF = requestAnimationFrame(function() { self._trackProgress(); });
  };

  proto._startProgress = function() {
    if (!this.progressEl) return;
    this._stopProgress();
    var self = this;
    this._progressRAF = requestAnimationFrame(function() { self._trackProgress(); });
  };

  proto._stopProgress = function() {
    if (this._progressRAF) {
      cancelAnimationFrame(this._progressRAF);
      this._progressRAF = null;
    }
  };

  /* ---- Private: Init ---- */

  proto._init = function() {
    var type = getVideoType(this.videoUrl);
    if (!type) return;

    this._initialized = true;

    /* Click overlay — sits above iframe for pause/resume */
    this._overlay = document.createElement('div');
    this._overlay.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;z-index:10;cursor:pointer;';
    this.playerEl.appendChild(this._overlay);

    var self = this;
    this._overlay.addEventListener('click', function(e) {
      e.stopPropagation();
      self._toggle();
    });

    switch (type) {
      case 'youtube': this._initYouTube(getYouTubeId(this.videoUrl)); break;
      case 'vimeo':   this._initVimeo(getVimeoId(this.videoUrl)); break;
      case 'wistia':  this._initWistia(getWistiaId(this.videoUrl)); break;
      default:        this._initDirect(this.videoUrl); break;
    }
  };

  /* ---- Private: Toggle ---- */

  proto._toggle = function() {
    if (this._ytPlayer && typeof this._ytPlayer.getPlayerState === 'function') {
      if (this._ytPlayer.getPlayerState() === YT.PlayerState.PLAYING) {
        this._ytPlayer.pauseVideo();
      } else {
        this._ytPlayer.playVideo();
      }
    } else if (this._wistiaVideo) {
        console.log("clicked");
      if (this._wistiaVideo.state() === 'playing') {
        console.log("clicked here");
        this._wistiaVideo.pause();
      } else {
        console.log("clicked play");
        this._wistiaVideo.play();
      }
    } else if (this._videoEl) {
      if (this._videoEl.paused) this._videoEl.play();
      else this._videoEl.pause();
    }
  };

  /* ---- Private: YouTube ---- */

  proto._initYouTube = function(videoId) {
    var self = this;
    loadYTApi(function() {
      var div = document.createElement('div');
      div.id = 'lvp-yt-' + self.uid;
      self.playerEl.appendChild(div);

      self._ytPlayer = new YT.Player(div.id, {
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          disablekb: 1,
          playsinline: 1,
          fs: 0
        },
        events: {
          onReady: function(e) {
            e.target.playVideo();
            self._setPlaying(true);
          },
          onStateChange: function(e) {
            if (e.data === YT.PlayerState.PLAYING) {
              self._setPlaying(true);
            } else if (e.data === YT.PlayerState.PAUSED) {
              self._setPlaying(false);
            } else if (e.data === YT.PlayerState.ENDED) {
              self._setPlaying(false);
              if (self.progressEl) self.progressEl.style.width = '100%';
            }
          }
        }
      });
    });
  };

  /* ---- Private: Vimeo ---- */

  proto._initVimeo = function(videoId) {
    var iframe = document.createElement('iframe');
    iframe.src = 'https://player.vimeo.com/video/' + videoId + '?autoplay=1&controls=0&title=0&byline=0&portrait=0';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    this.playerEl.appendChild(iframe);
    this._setPlaying(true);
  };

  /* ---- Private: Wistia ---- */

  proto._initWistia = function(videoId) {
    var self = this;
    loadWistiaApi(function() {
      var div = document.createElement('div');
      div.className = 'wistia_embed wistia_async_' + videoId;
      div.style.cssText = 'width:100%;height:100%;position:absolute;top:0;left:0;';
      self.playerEl.appendChild(div);

      window._wq = window._wq || [];
      _wq.push({ id: videoId, options: {
        autoPlay: true,
        controlsVisibleOnLoad: false,
        playbar: false,
        fullscreenButton: false,
        settingsControl: false,
        volumeControl: false,
        smallPlayButton: false,
        playButton: false,
        endVideoBehavior: 'default',
        silentAutoPlay: false
      }, onReady: function(video) {
        self._wistiaVideo = video;
        self._setPlaying(true);

        video.bind('play', function() { self._setPlaying(true); });
        video.bind('pause', function() { self._setPlaying(false); });
        video.bind('end', function() {
          self._setPlaying(false);
          if (self.progressEl) self.progressEl.style.width = '100%';
        });
      }});
    });
  };

  /* ---- Private: Direct ---- */

  proto._initDirect = function(url) {
    var self = this;
    this._videoEl = document.createElement('video');
    this._videoEl.src = url;
    this._videoEl.autoplay = true;
    this._videoEl.playsInline = true;
    this._videoEl.controls = false;
    this._videoEl.style.objectFit = 'cover';
    this._videoEl.setAttribute('preload', 'none');

    this._videoEl.addEventListener('play', function() { self._setPlaying(true); });
    this._videoEl.addEventListener('pause', function() { self._setPlaying(false); });
    this._videoEl.addEventListener('ended', function() {
      self._setPlaying(false);
      if (self.progressEl) self.progressEl.style.width = '100%';
    });

    this.playerEl.appendChild(this._videoEl);
  };

  /* ---- Expose globally ---- */
  window.LandingVideoPlayer = LandingVideoPlayer;

})();
